import { useCallback } from 'react';
import type { Command } from '../../menu/types';
import { store } from '../../../app/store';
import { extractErrorMessage } from '../../../utils/errorUtils';
import { financeEmailHistoryActionApi } from '../api/financeEmailHistoryActionApi';
import { whenCommand } from '../commands/utils';
import { isInboundFinanceEmailHistoryId } from '../utils/financeEmailHistoryId';

type MailboxEndpoint =
  | 'softDeleteFinanceEmails'
  | 'restoreFinanceEmails'
  | 'markFinanceEmailsSpam'
  | 'unmarkFinanceEmailsSpam';

type UseFinanceEmailRowActionsArgs = {
  commands?: Command[];
  onRefresh?: () => void;
  requestConfirmation?: (message: string) => Promise<boolean>;
  onNotify: (message: string, severity: 'success' | 'error') => void;
};

const runRowAction = async (
  row: Record<string, unknown>,
  endpoint: MailboxEndpoint,
  confirmMessage: string,
  args: Pick<UseFinanceEmailRowActionsArgs, 'onRefresh' | 'requestConfirmation' | 'onNotify'>
) => {
  const historyId = String(row.Id ?? '').trim();
  if (!historyId) {
    return;
  }

  if (args.requestConfirmation) {
    const confirmed = await args.requestConfirmation(confirmMessage);
    if (!confirmed) {
      return;
    }
  }

  try {
    const message = await store
      .dispatch(financeEmailHistoryActionApi.endpoints[endpoint].initiate([historyId]))
      .unwrap();
    args.onNotify(message || 'Done', 'success');
    args.onRefresh?.();
  } catch (error) {
    args.onNotify(extractErrorMessage(error, 'Action failed.'), 'error');
  }
};

/**
 * Per-row finance email mailbox actions. Enabled only when the matching
 * commands exist on the grid (same pattern as deleteScreenshot).
 */
export function useFinanceEmailRowActions({
  commands,
  onRefresh,
  requestConfirmation,
  onNotify,
}: UseFinanceEmailRowActionsArgs) {
  const handleArchive = useCallback(
    async (row: Record<string, unknown>) => {
      await runRowAction(row, 'softDeleteFinanceEmails', 'Move this email to archive?', {
        onRefresh,
        requestConfirmation,
        onNotify,
      });
    },
    [onRefresh, requestConfirmation, onNotify]
  );

  const handleRestore = useCallback(
    async (row: Record<string, unknown>) => {
      await runRowAction(row, 'restoreFinanceEmails', 'Restore this email from archive?', {
        onRefresh,
        requestConfirmation,
        onNotify,
      });
    },
    [onRefresh, requestConfirmation, onNotify]
  );

  const handleMarkSpam = useCallback(
    async (row: Record<string, unknown>) => {
      const historyId = String(row.Id ?? '').trim();
      if (!isInboundFinanceEmailHistoryId(historyId)) {
        onNotify('Only inbound emails can be moved to spam.', 'error');
        return;
      }

      await runRowAction(row, 'markFinanceEmailsSpam', 'Move this email to spam?', {
        onRefresh,
        requestConfirmation,
        onNotify,
      });
    },
    [onRefresh, requestConfirmation, onNotify]
  );

  const handleUnmarkSpam = useCallback(
    async (row: Record<string, unknown>) => {
      const historyId = String(row.Id ?? '').trim();
      if (!isInboundFinanceEmailHistoryId(historyId)) {
        onNotify('Only inbound emails can be removed from spam.', 'error');
        return;
      }

      await runRowAction(row, 'unmarkFinanceEmailsSpam', 'Remove this email from spam?', {
        onRefresh,
        requestConfirmation,
        onNotify,
      });
    },
    [onRefresh, requestConfirmation, onNotify]
  );

  return {
    onArchiveFinanceEmail: whenCommand(commands, 'deletefinanceemail', handleArchive),
    onRestoreFinanceEmail: whenCommand(commands, 'restorefinanceemail', handleRestore),
    onMarkFinanceEmailSpam: whenCommand(commands, 'markfinanceemailspam', handleMarkSpam),
    onUnmarkFinanceEmailSpam: whenCommand(commands, 'unmarkfinanceemailspam', handleUnmarkSpam),
  };
}
